// src/context/AuthContext.js
import React, { createContext, useState, useEffect } from "react";
import API from "./api";

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [role, setRole] = useState(() => localStorage.getItem("role") || null);
  const [token, setToken] = useState(() => localStorage.getItem("accessToken") || null);
  const [adminToken, setAdminToken] = useState(() => localStorage.getItem("adminToken") || null);
  const [user, setUser] = useState(() => {
    const userJson = localStorage.getItem("user");
    if (!userJson || userJson === "undefined") {
      return null;
    }
    try {
      return JSON.parse(userJson);
    } catch (e) {
      console.warn("Invalid user JSON in localStorage, resetting:", userJson);
      localStorage.removeItem("user");
      return null;
    }
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (role) localStorage.setItem("role", role);
    else localStorage.removeItem("role");

    if (token) localStorage.setItem("accessToken", token);
    else localStorage.removeItem("accessToken");

    if (adminToken) localStorage.setItem("adminToken", adminToken);
    else localStorage.removeItem("adminToken");

    if (user) localStorage.setItem("user", JSON.stringify(user));
    else localStorage.removeItem("user");
  }, [role, token, adminToken, user]);

  // ✅ Check session on mount
  useEffect(() => {
    const verifySession = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const res = await API.get("/auth/me");
        setUser(res.data.user);
        setRole(res.data.user.role);
      } catch (err) {
        console.error("Session expired or invalid token:", err);
        // 🧹 Clear stale tokens/state
        setToken(null);
        setUser(null);
        setRole(null);
        setAdminToken(null);
        localStorage.clear();
        // ❌ Do NOT redirect here, just reset state
      } finally {
        setLoading(false);
      }

    };

    verifySession();
  }, [token]);

  // ✅ Login
  const login = async (credentials) => {
    try {
      const res = await API.post("/auth/login", credentials, { withCredentials: true });

      setToken(res.data.token);
      setUser(res.data.user);
      setRole(res.data.user.role);

      return { success: true, user: res.data.user };
    } catch (error) {
      console.error("Login failed:", error);
      return { success: false, message: error.response?.data?.message || "Login failed" };
    }
  };

  // ✅ Impersonate
  // ✅ Impersonate
  // ✅ Impersonate
  const impersonate = (newToken, client) => {
    // Save the *current admin token and user* before replacing
    const currentToken = localStorage.getItem("accessToken");
    const currentUser = localStorage.getItem("user");

    if (!localStorage.getItem("adminToken") && currentToken) {
      localStorage.setItem("adminToken", currentToken);
      setAdminToken(currentToken);
    }

    if (!localStorage.getItem("adminUser") && currentUser) {
      localStorage.setItem("adminUser", currentUser);
    }

    // Switch to impersonated client session
    setToken(newToken);
    setUser(client);
    setRole(client.role || "client");

    localStorage.setItem("accessToken", newToken);
    localStorage.setItem("user", JSON.stringify(client));
    localStorage.setItem("role", client.role || "client");
  };


  // ✅ End Impersonation (go back to admin)
  const endImpersonation = async () => {
    const savedAdminToken = localStorage.getItem("adminToken");
    const savedAdminUser = localStorage.getItem("adminUser");

    if (!savedAdminToken || !savedAdminUser) return false;

    try {
      const adminUser = JSON.parse(savedAdminUser);

      // ✅ Restore admin state
      setToken(savedAdminToken);
      setAdminToken(null);
      setUser(adminUser);
      setRole(adminUser.role);

      // ✅ Clean impersonation state
      localStorage.removeItem("adminToken");
      localStorage.removeItem("adminUser");

      // ✅ Save admin session back
      localStorage.setItem("user", JSON.stringify(adminUser));
      localStorage.setItem("role", adminUser.role);
      localStorage.setItem("accessToken", savedAdminToken);

    

      return true;
    } catch (err) {
      console.error("❌ Failed to restore admin session:", err);
      // 🚀 Instead of logout, redirect to login page
      localStorage.clear(); // optional: wipe broken state
      window.location.href = "/"; // login page
      return false;
    }
  };




  // ✅ Full Logout (clears everything, used by admin logout)
  const logout = async () => {
    try {
      await API.post("/auth/logout", {}, { withCredentials: true });
    } catch (err) {
      console.warn("Logout API call failed:", err);
    }

    setToken(null);
    setRole(null);
    setUser(null);
    setAdminToken(null);
    localStorage.clear();
    window.location.href = "/";
  };

  return (
    <AuthContext.Provider
      value={{
        role,
        token,
        user,
        login,
        logout,            // full logout
        impersonate,       // start impersonation
        endImpersonation,  // stop impersonation
        isAuthenticated: !!token,
        loading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
